import { Col, Flex, Modal, Row, Table, TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// components
import Icon from 'utils/icon';
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';
import ListButton from 'components/listButton/listButton';
import TableTotal from 'components/tableTotal/tableTotal';
import FilePreview from 'components/filePreview/filePreview';
import DisabledElement from 'components/disabledElement/disabledElement';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';

// services
import { isRTL } from 'core/services/language';
import { formatFileSize } from 'utils/FileSize';
import NotificationService from 'core/services/notification';
import { onDeleteCommitteeDocument, onGetCommitteeDocumentById, onGetCommitteeDocumentsList } from '../services/documentsServices';

// models
import { CommitteeDocument } from '../models/documents';

// utils
import { StatusColors } from 'utils/statusColors';
import { dateFormat } from 'utils/dateFormat';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

interface CommitteeDocumentsProps {
	committeeId: number;
	refresh?(): void;
}

const CommitteeDocuments: FunctionComponent<CommitteeDocumentsProps> = ({ committeeId, refresh }) => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [documents, setDocuments] = useState<CommitteeDocument[]>([]);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalCount, setTotalCount] = useState<number>(0);

	const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(false);
	const [documentDetails, setDocumentDetails] = useState<CommitteeDocument>();
	const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);

	const [tempDeletedDocumentId, setTempDeletedDocumentId] = useState<number>();
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

	const columns: TableProps<CommitteeDocument>['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: t('ACTIONS.TITLE'),
			dataIndex: 'translated_title',
			key: 'title',
			render: (translated_title) => <span>{isRTL() ? translated_title.ar : translated_title.en}</span>,
		},
		{
			title: t('COMMON.DESCRIPTION'),
			dataIndex: 'translated_description',
			key: 'description',
			render: (translated_description) => <span>{isRTL() ? translated_description.ar : translated_description.en}</span>,
		},
		{
			title: t('ACTIONS.FILES_COUNT'),
			dataIndex: 'files_count',
			key: 'files_count',
		},
		{
			title: t('COMMON.CREATED_AT'),
			dataIndex: 'created_at',
			key: 'created_at',
			render: (created_at) => <span>{dateFormat(created_at)}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '_',
			key: '_',
			render: (_, row) => (
				<Flex gap={10} justify="center">
					<ListButton
						type="view"
						text={t('COMMON.VIEW')}
						onClick={() => {
							getDocumentHandler(row.id);
							setIsDetailsModalOpen(true);
						}}
					/>

					<ListButton
						type="edit"
						{...suspendedProps}
						onClick={() => navigate(`/dashboard/actions/boards/committees/${committeeId}/documents/${row.id}`)}
					/>

					<ListButton
						type="delete"
						{...suspendedProps}
						onClick={() => {
							setTempDeletedDocumentId(row.id);
							setIsDeleteModalOpen(true);
						}}
					/>
				</Flex>
			),
		},
	];

	useEffect(() => {
		getCommitteeDocuments();
	}, []);

	const getCommitteeDocuments = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCommitteeDocumentsList(committeeId, 1);
			setDocuments(response.documents);
			setTotalCount(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
		}
	};

	const fetchMoreMeetingData = async (page: number) => {
		try {
			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetCommitteeDocumentsList(committeeId, page);
			setDocuments(response.documents);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
		}
	};

	const getDocumentHandler = async (id: number) => {
		try {
			setIsDocumentLoading(true);

			const response = await onGetCommitteeDocumentById(committeeId.toString(), id.toString());

			setDocumentDetails(response);

			setIsDocumentLoading(false);
		} catch (error) {
			setIsDocumentLoading(false);
			setIsDetailsModalOpen(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const deleteDocumentHandler = async () => {
		try {
			if (!tempDeletedDocumentId) return;

			setIsDeleting(true);

			await onDeleteCommitteeDocument(committeeId.toString(), tempDeletedDocumentId.toString());

			getCommitteeDocuments();

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.DOCUMENT_DELETED_SUCCESSFULLY'));

			setIsDeleting(false);
			setIsDeleteModalOpen(false);
			setTempDeletedDocumentId(undefined);
		} catch (error) {
			setIsDeleting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.ERROR_DELETING_DOCUMENT'));
		}
	};

	return (
		<>
			<Table
				columns={columns}
				dataSource={documents}
				className="bordered mb-4"
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				pagination={{
					pageSize: 15,
					total: totalCount,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreMeetingData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
				sticky
			/>

			{/* details modal  */}
			<Modal
				width={{
					sm: '90%',
					md: '80%',
					lg: '80%',
					xl: '50%',
					xxl: '40%',
				}}
				open={isDetailsModalOpen}
				title={
					<Flex gap={10} align="center">
						<Icon name="info" width={30} height={30} />
						<h6 className="mb-0">{t('ACTIONS.DOCUMENT_DETAILS')}</h6>
					</Flex>
				}
				footer={[]}
				onCancel={() => {
					setIsDetailsModalOpen(false);
					setDocumentDetails(undefined);
				}}>
				{isDocumentLoading ? (
					<LoadingComponent />
				) : (
					<Row gutter={[16, 16]} className="mt-4">
						<Col span={24} md={12}>
							<DisabledElement fullWidth label={t('ACTIONS.TITLE_IN_AR')} value={documentDetails?.translated_title.ar} />
						</Col>
						<Col span={24} md={12}>
							<DisabledElement fullWidth label={t('ACTIONS.TITLE_IN_EN')} value={documentDetails?.translated_title.en} />
						</Col>
						<Col span={24} md={12}>
							<DisabledElement fullWidth label={t('ACTIONS.DESCRIPTION_IN_ARABIC')} value={documentDetails?.translated_description.ar} />
						</Col>
						<Col span={24} md={12}>
							<DisabledElement fullWidth label={t('ACTIONS.DESCRIPTION_IN_ENGLISH')} value={documentDetails?.translated_description.en} />
						</Col>
						<Col span={24}>
							{documentDetails?.files.map((file) => (
								<FilePreview
									key={file.id}
									name={file.title}
									downloadLink={file.download_link}
									data={formatFileSize(file.size_in_kilobyte)}
									className="mb-3"
								/>
							))}
						</Col>
					</Row>
				)}
			</Modal>

			{/* delete modal */}
			<ConfirmModalComponent
				open={isDeleteModalOpen}
				confirmText={t('COMMON.DELETE')}
				confirmColor="danger"
				onConfirm={deleteDocumentHandler}
				onDiscard={() => setIsDeleteModalOpen(false)}
				title={t('ACTIONS.DELETE_DOCUMENT')}
				description={t('ACTIONS.CONFIRM_DELETE_DOCUMENT')}
				isLoading={isDeleting}
			/>
		</>
	);
};

export default CommitteeDocuments;
